import { jsPDF } from 'jspdf';

const amount = (value) => Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const dateLabel = (value) => new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric'
});

const download = (blob, fileName) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getRows = (report, viewMode) => {
  const rows = [];
  const sections = [
    ['operating', 'Operating Activities'],
    ['investing', 'Investing Activities'],
    ['financing', 'Financing Activities']
  ];

  sections.forEach(([key, label]) => {
    rows.push([label, '', '']);

    Object.entries(report[key].groups).forEach(([groupName, group]) => {
      rows.push([groupName, '', amount(group.total)]);

      if (viewMode === 'details') {
        group.accounts.forEach((account) => {
          rows.push(['', account.name, amount(account.amount)]);
        });
      }

      rows.push([`Total ${groupName}`, '', amount(group.total)]);
    });

    rows.push([`Net Cash from ${label}`, '', amount(report[key].total)]);
  });

  rows.push(['Overview', '', '']);
  rows.push(['Starting Balance', '', amount(report.beginningCash)]);
  rows.push(['Gross Cash Inflow', '', amount(report.grossInflow)]);
  rows.push(['Gross Cash Outflow', '', amount(report.grossOutflow)]);
  rows.push(['Net Cash Change', '', amount(report.netChange)]);
  rows.push(['Ending Balance', '', amount(report.endingCash)]);

  return rows;
};

export const downloadCashFlowCsv = ({ report, startDate, endDate, viewMode }) => {
  const rows = [
    ['Cash Inflow and Outflow Statement', '', ''],
    [`For ${dateLabel(startDate)} through ${dateLabel(endDate)}`, '', ''],
    ['Line Item', 'Account', 'Amount'],
    ...getRows(report, viewMode)
  ];

  const csv = `\uFEFF${rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\r\n')}`;

  download(
    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
    `Cash_Flow_${startDate}_${endDate}_${viewMode}.csv`
  );
};

export const downloadCashFlowPdf = ({ report, startDate, endDate, viewMode }) => {
  const PdfConstructor = typeof jsPDF === 'function' ? jsPDF : window.jspdf?.jsPDF;
  if (typeof PdfConstructor !== 'function') throw new Error('PDF export is unavailable.');

  const pdf = new PdfConstructor({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  const margin = 48;
  let y = margin;

  const writeHeader = () => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('Cash Inflow and Outflow Statement', width / 2, y, { align: 'center' });
    y += 20;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`For ${dateLabel(startDate)} through ${dateLabel(endDate)}`, width / 2, y, { align: 'center' });
    y += 24;
    pdf.setDrawColor(37, 99, 235);
    pdf.line(margin, y, width - margin, y);
    y += 18;
  };

  const ensureSpace = () => {
    if (y > height - 55) {
      pdf.addPage();
      y = margin;
      writeHeader();
    }
  };

  const line = (label, value, options = {}) => {
    ensureSpace();
    pdf.setFont('helvetica', options.bold ? 'bold' : 'normal');
    pdf.setFontSize(options.size || 9);
    pdf.setFillColor(options.shade ? 241 : 255, options.shade ? 245 : 255, options.shade ? 249 : 255);
    if (options.shade) pdf.rect(margin, y - 11, width - margin * 2, 18, 'F');
    pdf.text(label, margin + (options.indent || 0), y);
    pdf.text(amount(value), width - margin, y, { align: 'right' });
    y += options.gap || 18;
  };

  writeHeader();
  const sections = [
    ['operating', 'Operating Activities'],
    ['investing', 'Investing Activities'],
    ['financing', 'Financing Activities']
  ];

  sections.forEach(([key, label]) => {
    line(label.toUpperCase(), 0, { bold: true, shade: true, size: 10, gap: 24 });

    Object.entries(report[key].groups).forEach(([groupName, group]) => {
      line(groupName, 0, { bold: true, indent: 10, gap: 16 });

      if (viewMode === 'details') {
        group.accounts.forEach((account) => {
          line(account.name, account.amount, { indent: 24 });
        });
      }

      line(`Total ${groupName}`, group.total, { bold: true, shade: true, indent: 10, gap: 22 });
    });

    line(`Net Cash from ${label}`, report[key].total, { bold: true, gap: 26 });
  });

  line('OVERVIEW', 0, { bold: true, shade: true, size: 10, gap: 22 });
  line('Starting Balance', report.beginningCash);
  line('Gross Cash Inflow', report.grossInflow);
  line('Gross Cash Outflow', -report.grossOutflow);
  line('Net Cash Change', report.netChange, { bold: true, shade: true });
  line('Ending Balance', report.endingCash, { bold: true, size: 11 });

  const pages = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(100, 116, 139);
    pdf.text(`Page ${page} of ${pages}`, width - margin, height - 22, { align: 'right' });
  }

  pdf.save(`Cash_Flow_${startDate}_${endDate}_${viewMode}.pdf`);
};